import {
  getFieldId,
  getFieldOptions,
  getFieldType,
  getFieldTypeConfig,
  isFieldMultiple,
  isLiveSearchEnabledField,
} from '../form/formUseCase';
import { encodeFieldName } from '../form/fieldNameCodec';

/** @type {string} */
const EMPTY_DISPLAY_VALUE = '—';

/** @type {number} */
const LIVE_SEARCH_MIN_QUERY_LENGTH = 3;

/**
 * Escape special regex characters in a string.
 * @param {string} value
 * @returns {string}
 */
function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Extract deduplicated live search query terms from a request body.
 * @param {ApiLiveSearchRequest} body
 * @returns {string[]}
 */
export function getLiveSearchQueryTerms(body) {
  /** @type {string[]} */
  const terms = [];
  const seen = new Set();

  body.searchFields.forEach((field) => {
    const term = field.value.trim();
    if (term.length < LIVE_SEARCH_MIN_QUERY_LENGTH) return;

    const key = term.toLowerCase();
    if (!key || seen.has(key)) return;

    seen.add(key);
    terms.push(term);
  });

  return terms;
}

/**
 * Split text into segments for highlight rendering.
 * Matching is case-insensitive. Overlaps prefer longer, then earlier matches.
 * @param {string} text
 * @param {string[]} terms
 * @returns {Array<{ text: string, matched: boolean }>}
 */
export function highlightTextSegments(text, terms) {
  if (text === '' || text === EMPTY_DISPLAY_VALUE || !terms?.length) {
    return [{ text, matched: false }];
  }

  const needles = [...new Set(
    terms
      .map((term) => term.trim())
      .filter(Boolean),
  )].sort((a, b) => b.length - a.length);

  if (needles.length === 0) {
    return [{ text, matched: false }];
  }

  const pattern = new RegExp(needles.map(escapeRegExp).join('|'), 'gi');
  /** @type {Array<{ text: string, matched: boolean }>} */
  const segments = [];
  let cursor = 0;

  Array.from(text.matchAll(pattern)).forEach((match) => {
    const start = /** @type {number} */ (match.index);
    if (start > cursor) {
      segments.push({ text: text.slice(cursor, start), matched: false });
    }
    segments.push({ text: match[0], matched: true });
    cursor = start + match[0].length;
  });

  if (cursor < text.length) {
    segments.push({ text: text.slice(cursor), matched: false });
  }

  return segments.length > 0 ? segments : [{ text, matched: false }];
}

/**
 * Format a live search result value for display in a card.
 * @param {string | null} value
 * @returns {string}
 */
export function formatLiveSearchDisplayValue(value) {
  if (value === null || value === '') {
    return EMPTY_DISPLAY_VALUE;
  }
  return value;
}

/**
 * Format a live search row label for display above the value pastille.
 * Empty/null labels are omitted in the card (no "—" placeholder).
 * Trailing colons from legacy payloads are stripped.
 * @param {string | null} text
 * @returns {string}
 */
export function formatLiveSearchLabelText(text) {
  if (text === null || text === '') {
    return '';
  }
  return text.replace(/:\s*$/, '').trim();
}

/**
 * Map an API text+icon cell side to card display props.
 * @param {ApiLiveSearchTextWithIcon} side
 * @param {'label' | 'value'} [role='value']
 * @returns {LiveSearchResultCardTextProps}
 */
function mapLiveSearchTextWithIcon(side, role = 'value') {
  return {
    text: role === 'label'
      ? formatLiveSearchLabelText(side.text)
      : formatLiveSearchDisplayValue(side.text),
    icon: side.icon ?? null,
    iconColor: side.iconColor ?? null,
  };
}

/**
 * Map an API live search result to LiveSearchResultCard UI props.
 * @param {ApiLiveSearchResult} result
 * @returns {LiveSearchResultCardProps}
 */
export function mapLiveSearchResultToCardProps(result) {
  return {
    title: result.title,
    subTitle: result.subTitle ?? null,
    isActive: result.actif,
    rows: (result.rows ?? []).map((row) => ({
      values: (row.values ?? []).map((cell) => ({
        label: mapLiveSearchTextWithIcon(cell.lib, 'label'),
        value: mapLiveSearchTextWithIcon(cell.value, 'value'),
      })),
    })),
    actions: result.actions.map((action) => ({
      label: action.lib,
      icon: action.icon,
    })),
  };
}

/**
 * Whether the given form configuration enables live search (form-level toggle).
 * Single source of truth for `hasLiveSearch` across UI and hooks.
 * @param {ApiFormResponse | null | undefined} formConfig
 * @returns {boolean}
 */
export function hasFormLiveSearch(formConfig) {
  return Boolean(formConfig?.searchUrl);
}

/**
 * Collect field ids with liveSearchEnabled on text fields.
 * @param {ApiFormResponse} formConfig
 * @returns {string[]}
 */
export function getLiveSearchFieldIds(formConfig) {
  /** @type {string[]} */
  const fieldIds = [];

  formConfig.tabs.forEach((tab) => {
    tab.fieldsets.forEach((fieldset) => {
      fieldset.fields.forEach((field) => {
        if (!isLiveSearchEnabledField(field)) return;
        const fieldId = getFieldId(field);
        if (fieldId) fieldIds.push(fieldId);
      });
    });
  });

  return fieldIds;
}

/**
 * Serialize a form field value for the live search POST body.
 * @param {unknown} rawValue
 * @returns {string}
 */
export function serializeLiveSearchFieldValue(rawValue) {
  if (rawValue === null || rawValue === undefined) {
    return '';
  }

  if (Array.isArray(rawValue)) {
    return rawValue
      .map((item) => {
        if (typeof item === 'object' && item !== null && 'value' in item) {
          return String(/** @type {{ value: string }} */ (item).value);
        }
        return String(item);
      })
      .join(', ');
  }

  if (typeof rawValue === 'object' && rawValue !== null && Array.isArray(
    /** @type {{ values?: unknown }} */ (rawValue).values,
  )) {
    const entityValues = /** @type {{ values: unknown[] }} */ (rawValue).values;
    return entityValues.map((item) => String(item)).join(', ');
  }

  if (typeof rawValue === 'object' && rawValue !== null && 'value' in rawValue) {
    const scalarValue = /** @type {{ value?: string }} */ (rawValue).value;
    return scalarValue ?? '';
  }

  return String(rawValue);
}

/**
 * Build live search request body from current form values.
 * @param {ApiFormResponse} formConfig
 * @param {Record<string, unknown>} formValues
 * @param {Set<string>} [visibleFieldIds] - When set, only visible live search fields are sent
 * @returns {ApiLiveSearchRequest}
 */
export function buildLiveSearchRequest(formConfig, formValues, visibleFieldIds) {
  const liveSearchFieldIds = getLiveSearchFieldIds(formConfig);

  const searchFields = liveSearchFieldIds
    .filter((fieldId) => {
      const rhfName = encodeFieldName(fieldId);
      return !visibleFieldIds || visibleFieldIds.has(rhfName);
    })
    .map((fieldId) => ({
      id: fieldId,
      value: serializeLiveSearchFieldValue(formValues[encodeFieldName(fieldId)]),
    }));

  return { searchFields };
}

/**
 * Whether the request has at least one search value long enough to trigger live search.
 * @param {ApiLiveSearchRequest} body
 * @returns {boolean}
 */
export function hasLiveSearchQuery(body) {
  return body.searchFields.some(
    (field) => field.value.trim().length >= LIVE_SEARCH_MIN_QUERY_LENGTH,
  );
}

/**
 * Whether at least one live search field has been modified by the user.
 * @param {import('react-hook-form').FieldNamesMarkedBoolean<Record<string, unknown>>} dirtyFields
 * @param {string[]} liveSearchFieldIds
 * @returns {boolean}
 */
export function hasDirtyLiveSearchFields(dirtyFields, liveSearchFieldIds) {
  return liveSearchFieldIds.some((fieldId) => Boolean(dirtyFields[encodeFieldName(fieldId)]));
}

/**
 * Whether form live search should POST given dirty state and request body.
 * @param {object} params
 * @param {import('react-hook-form').FieldNamesMarkedBoolean<Record<string, unknown>>} params.dirtyFields
 * @param {string[]} params.liveSearchFieldIds
 * @param {ApiLiveSearchRequest} params.body
 * @returns {boolean}
 */
export function shouldRunFormLiveSearch({ dirtyFields, liveSearchFieldIds, body }) {
  if (!hasDirtyLiveSearchFields(dirtyFields, liveSearchFieldIds)) return false;
  return hasLiveSearchQuery(body);
}

/**
 * Resolve how a live search action should be handled.
 * If both URLs are non-null (off-spec), autoSubmit takes priority (defensive).
 * @param {ApiLiveSearchResultAction} action
 * @returns {LiveSearchActionMode | null}
 */
export function getLiveSearchActionMode(action) {
  const { urls } = action;
  if (urls?.autoSubmitUrl) return 'autoSubmit';
  if (urls?.fieldsUrl) return 'fill';
  return null;
}

/**
 * Returns the active URL for fetch/auto-submit actions (autoSubmitUrl takes priority).
 * @param {ApiLiveSearchResultAction} action
 * @returns {string | null}
 */
export function getLiveSearchActionUrl(action) {
  const { urls } = action;
  if (urls?.autoSubmitUrl) return urls.autoSubmitUrl;
  if (urls?.fieldsUrl) return urls.fieldsUrl;
  return null;
}

/**
 * Map a livesearch-fill action field to a React Hook Form value.
 * Uses layout field type (not only API singleValue) and type-specific parseValue,
 * aligned with buildDefaultValues / applyFieldsToUpdate.
 * @param {FormItem} field
 * @param {ApiLiveSearchActionField} actionField
 * @returns {unknown}
 */
export function mapLiveSearchActionFieldToFormValue(field, actionField) {
  const fieldType = getFieldType(field);
  const fieldTypeConfig = getFieldTypeConfig(fieldType);
  const apiValues = Array.isArray(actionField.values) ? actionField.values : [];

  if (fieldType === 'entity') {
    return fieldTypeConfig.parseValue({
      values: apiValues.filter((val) => val != null && val !== ''),
      displayedValues: [],
    });
  }

  if (isFieldMultiple(field)) {
    const fieldOptions = getFieldOptions(field);
    return apiValues
      .filter((val) => val != null && val !== '')
      .map((val) => {
        const matchingOption = fieldOptions.find((opt) => opt.id === val);
        return {
          value: val,
          label: matchingOption?.lib ?? val,
        };
      });
  }

  if (fieldType === 'mergeTable') {
    return fieldTypeConfig.parseValue(apiValues);
  }

  const rawScalar = apiValues[0] ?? '';
  return fieldTypeConfig.parseValue(rawScalar);
}

/**
 * Apply live search field values to the form.
 * @param {object} params
 * @param {ApiLiveSearchActionField[]} params.fields
 * @param {import('react-hook-form').UseFormSetValue<Record<string, unknown>>} params.setValue
 * @param {FormLayout} params.layout
 */
export function applyLiveSearchFields({
  fields,
  setValue,
  layout,
}) {
  fields.forEach((actionField) => {
    const field = layout.fieldsById[actionField.id];
    if (!field) return;

    const nextValue = mapLiveSearchActionFieldToFormValue(field, actionField);
    setValue(encodeFieldName(actionField.id), nextValue, { shouldDirty: true, shouldValidate: true });
  });
}

/**
 * Format a livesearch-fill API error for display.
 * @param {unknown} error
 * @returns {string}
 */
export function getLiveSearchFillErrorMessage(error) {
  const apiError = /** @type {{ errors?: { message: string }[], message?: string }} */ (error);
  if (apiError?.errors?.length) {
    return apiError.errors.map((item) => item.message).join(' ');
  }
  return apiError?.message ?? '';
}
